"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/usePlaidConnectionsPanel";

import type {
  PlaidBindSuccessContext,
  PlaidImportModalMode,
  PlaidImportModalResult,
} from "./types";
import { useCallback, useState } from "react";

import { OspToast } from "@ospweb/sdk-ui";
import type { FinancialConnection } from "@features/api/memberApi";
import {
  connectionHasNonInvestmentAccount,
  type PlaidMemberAuthorizationSuccessOutcome,
} from "@/src/hooks/plaid/memberPlaidApi";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { useMemoizedFn } from "ahooks";
import { usePortfolioConnections } from "@/src/hooks/plaid/usePortfolioConnections";
import { usePlaidLinkFlow } from "@/src/hooks/plaid/usePlaidLinkFlow";

const I18n = I18nUtil.createScoped(Translations);

export interface UsePlaidConnectionsPanelOptions {
  mode?: PlaidImportModalMode;
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  onClose?: (result: PlaidImportModalResult) => void;
}

export interface UsePlaidConnectionsPanelResult {
  investConnections: FinancialConnection[];
  nonInvestConnections: FinancialConnection[];
  isLoadingConnections: boolean;
  forceConnectionsView: boolean;
  linkLoading: boolean;
  binding: boolean;
  webLinkHost: ReturnType<typeof usePlaidLinkFlow>["webLinkHost"];
  rebindUpdateLinkToken: string | null;
  handleRebindCancel: () => void;
  handleRebindAdd: () => void;
  onAddOthers: () => void;
  onUpdateConnection: (connectionId: string) => void;
  bindingWithDelay: boolean;
}

export const usePlaidConnectionsPanel = (
  options: UsePlaidConnectionsPanelOptions = {},
): UsePlaidConnectionsPanelResult => {
  const { mode = "default", onBindSuccess, onClose } = options;
  const [forceConnectionsView, setForceConnectionsView] = useState(false);
  const [rebindUpdateLinkToken, setRebindUpdateLinkToken] = useState<
    string | null
  >(null);
  const [bindingWithDelay, setBindingWithDelay] = useState(false);
  const { investConnections, nonInvestConnections, isLoading } =
    usePortfolioConnections();

  const handleBindSuccess = useMemoizedFn(
    async (outcome: PlaidMemberAuthorizationSuccessOutcome) => {
      setBindingWithDelay(true);

      // 延迟2秒后再调用onBindSuccess
      await new Promise((resolve) => setTimeout(resolve, 2000));

      await onBindSuccess?.({
        connection: outcome.connection,
        connectionId: outcome.connectionId,
      });

      if (connectionHasNonInvestmentAccount(outcome.connection)) {
        OspToast.info({
          content: I18n.t("Portfolio shows your investment accounts only"),
        });
      }

      if (mode === "once") {
        onClose?.({ action: "success" });
        setBindingWithDelay(false);
        return;
      }
      setForceConnectionsView(true);
      OspToast.success({ content: I18n.t("Binding successful") });
      setBindingWithDelay(false);
      onClose?.({ action: "success" });
    },
  );

  const handleBindError = useCallback(
    (error: unknown) => {
      trackOnboardingAnalytics("ob_funnel_plaid_downgrade");
      if (mode === "once") {
        onClose?.({ action: "error", error });
        return;
      }
      const message =
        error instanceof Error
          ? error.message
          : String(error ?? "Unknown error");
      OspToast.error({
        content: I18n.t("Binding failed: %{var0}", {
          var0: message,
        }),
      });
    },
    [mode, onClose],
  );

  const handleLinkExit = useMemoizedFn(() => {
    if (mode === "once") {
      onClose?.({ action: "exit" });
    }
  });

  const handleInstitutionAlreadyLinked = useMemoizedFn(
    (updateLinkToken: string) => {
      setRebindUpdateLinkToken(updateLinkToken);
    },
  );

  const {
    loading: linkLoading,
    binding,
    openLink,
    openLinkWithToken,
    webLinkHost,
  } = usePlaidLinkFlow({
    autoInit: false,
    onBindSuccess: handleBindSuccess,
    onBindError: handleBindError,
    onLinkExit: handleLinkExit,
    onInstitutionAlreadyLinked: handleInstitutionAlreadyLinked,
  });

  const handleRebindCancel = useMemoizedFn(() => {
    setRebindUpdateLinkToken(null);
    if (mode === "once") {
      onClose?.({ action: "exit" });
    }
  });

  const handleRebindAdd = useMemoizedFn(() => {
    const token = rebindUpdateLinkToken;
    setRebindUpdateLinkToken(null);
    if (token) {
      void openLinkWithToken(token);
    }
  });

  const onAddOthers = useMemoizedFn(() => {
    void openLink();
  });

  const onUpdateConnection = useMemoizedFn((connectionId: string) => {
    void openLink({ connectionId });
  });

  return {
    investConnections,
    nonInvestConnections,
    isLoadingConnections: isLoading,
    forceConnectionsView,
    linkLoading,
    binding,
    webLinkHost,
    rebindUpdateLinkToken,
    handleRebindCancel,
    handleRebindAdd,
    onAddOthers,
    onUpdateConnection,
    bindingWithDelay,
  };
};
